import * as THREE from 'three';

/**
 * An analysed room, as the visualizer reads it.
 *
 * Ported from ai-visualizer (scenePuzzle.ts, DepthOccluder.tsx,
 * FurnitureInteractionManager's floor plane) so Content Studio places a product
 * on a room exactly as the visualizer does: the camera at the origin looking
 * down -Z with Y up, the room's own vertical field of view, the floor as a
 * plane equation in metres, and the room's depth as an invisible mesh that
 * hides whatever stands behind the furniture already in the picture.
 *
 * py-visualizer's /scene arrives decrypted from the API; its masks and depth
 * are still tile-shuffled, with the permutation alongside.
 */

export interface PuzzleMeta {
    /** perm[i] = original tile index now sitting at scrambled position i. */
    p: number[];
    c: number;
    r: number;
    w: number;
    h: number;
}

export interface SceneSurface {
    type: 'floor' | 'wall' | 'ceiling' | string;
    id: number;
    plane: [number, number, number, number];
    centroid: [number, number, number];
    normal: [number, number, number];
    mask?: string;
    mask_pz?: PuzzleMeta | null;
}

export interface RawRoomScene {
    fov: number;
    aspect_ratio: number;
    surfaces: SceneSurface[];
    image: string;
    depth_map?: string;
    _pz?: { depth?: PuzzleMeta; shading?: PuzzleMeta };
}

export interface RoomScene {
    fov: number;
    aspect: number;
    /** The room as an image the canvas can read back (a data URL — never tainted). */
    imageUrl: string;
    width: number;
    height: number;
    floor: SceneSurface;
    floorPlane: THREE.Plane;
    floorNormal: THREE.Vector3;
    /** Invisible depth mesh, floor cut out; null when the room has no depth. */
    occluder: THREE.BufferGeometry | null;
}

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('A room image could not be decoded'));
        img.src = src;
    });
}

/** Scrambled tile i holds original tile perm[i]; copy each back to its slot, losslessly. */
function unscramble(img: CanvasImageSource, meta: PuzzleMeta): HTMLCanvasElement {
    const { p, c: cols, r: rows, w: W, h: H } = meta;
    const tw = Math.ceil(W / cols);
    const th = Math.ceil(H / rows);
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = false;
    for (let i = 0; i < p.length; i++) {
        const src = p[i];
        const dr = Math.floor(i / cols), dc = i % cols;
        const sr = Math.floor(src / cols), sc = src % cols;
        ctx.drawImage(img, dc * tw, dr * th, tw, th, sc * tw, sr * th, tw, th);
    }
    return canvas;
}

async function pngToCanvas(b64: string, meta?: PuzzleMeta | null): Promise<HTMLCanvasElement> {
    const img = await loadImage(`data:image/png;base64,${b64}`);
    if (meta && Array.isArray(meta.p) && meta.p.length) return unscramble(img, meta);
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    canvas.getContext('2d')!.drawImage(img, 0, 0);
    return canvas;
}

/** The floor plane, normal pointing up, as three.js wants it. */
export function planeFrom(plane: [number, number, number, number]): THREE.Plane {
    const [a, b, c, d] = plane;
    const n = new THREE.Vector3(a, b, c);
    const len = n.length() || 1;
    return new THREE.Plane(n.normalize(), d / len);
}

/**
 * The depth map as an invisible mesh — DepthOccluder, faithfully.
 *
 * Depth is camera-Z in millimetres, R high byte and G low byte, downsampled to
 * 512 wide; invalid pixels read 100 m. Each pixel is unprojected through the
 * ROOM's projection (not the live camera) and the floor is cut out, so the
 * product can lie on it while sofas and tables in front still hide it.
 */
function buildOccluder(
    depthCanvas: HTMLCanvasElement,
    floorMasks: HTMLCanvasElement[],
    fov: number,
    aspect: number,
): THREE.BufferGeometry | null {
    const w = depthCanvas.width;
    const h = depthCanvas.height;
    if (!w || !h) return null;
    const px = depthCanvas.getContext('2d')!.getImageData(0, 0, w, h).data;

    // Floor pixels at depth resolution. The mask is inverted: surface = black.
    const isFloor = new Uint8Array(w * h);
    if (floorMasks.length) {
        const m = document.createElement('canvas');
        m.width = w;
        m.height = h;
        const mc = m.getContext('2d')!;
        mc.imageSmoothingEnabled = false;
        for (const mask of floorMasks) {
            mc.clearRect(0, 0, w, h);
            mc.drawImage(mask, 0, 0, w, h);
            const md = mc.getImageData(0, 0, w, h).data;
            for (let i = 0; i < w * h; i++) if (md[i * 4] < 127) isFloor[i] = 1;
        }
    }

    const invProj = new THREE.PerspectiveCamera(fov, aspect, 0.1, 1000).projectionMatrixInverse;
    const positions = new Float32Array(w * h * 3);
    const valid = new Uint8Array(w * h);
    const clip = new THREE.Vector4();
    const ray = new THREE.Vector3();
    for (let row = 0; row < h; row++) {
        for (let col = 0; col < w; col++) {
            const i = row * w + col;
            const d = (px[i * 4] * 256 + px[i * 4 + 1]) / 1000;
            if (d <= 0 || d >= 95 || isFloor[i]) continue;
            const u = (col + 0.5) / w;
            const v = (row + 0.5) / h;
            clip.set(u * 2 - 1, -(v * 2 - 1), -1, 1).applyMatrix4(invProj);
            ray.set(clip.x / clip.w, clip.y / clip.w, clip.z / clip.w).normalize();
            const t = -d / ray.z;
            positions[i * 3] = ray.x * t;
            positions[i * 3 + 1] = ray.y * t;
            positions[i * 3 + 2] = ray.z * t;
            valid[i] = 1;
        }
    }
    const index: number[] = [];
    for (let row = 0; row < h - 1; row++) {
        for (let col = 0; col < w - 1; col++) {
            const tl = row * w + col, tr = tl + 1, bl = tl + w, br = bl + 1;
            if (valid[tl] && valid[tr] && valid[bl] && valid[br]) index.push(tl, bl, tr, tr, bl, br);
        }
    }
    if (!index.length) return null;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setIndex(index);
    return geo;
}

export async function prepareRoomScene(raw: RawRoomScene): Promise<RoomScene> {
    const floors = (raw.surfaces || []).filter((s) => s.type === 'floor');
    if (!floors.length) throw new Error('No floor was found in this room');
    // The largest floor region is the one a product stands on; masks tell us
    // which that is only once decoded, so the first (sorted by name) serves.
    const floor = floors[0];

    const imageUrl = `data:image/png;base64,${raw.image}`;
    const img = await loadImage(imageUrl);

    let occluder: THREE.BufferGeometry | null = null;
    if (raw.depth_map) {
        try {
            const depth = await pngToCanvas(raw.depth_map, raw._pz?.depth);
            const masks = await Promise.all(
                floors.filter((f) => f.mask).map((f) => pngToCanvas(f.mask!, f.mask_pz)),
            );
            occluder = buildOccluder(depth, masks, raw.fov, raw.aspect_ratio);
        } catch {
            // No occlusion is a worse picture, not a broken one.
            occluder = null;
        }
    }

    const floorPlane = planeFrom(floor.plane);
    return {
        fov: raw.fov,
        aspect: raw.aspect_ratio,
        imageUrl,
        width: img.naturalWidth,
        height: img.naturalHeight,
        floor,
        floorPlane,
        floorNormal: floorPlane.normal.clone(),
        occluder,
    };
}

/**
 * The point on the floor along the ray from the camera through `point` —
 * snapPointToSurfacePlane, so a centroid that sits off the plane lands on it.
 */
export function snapToFloor(point: THREE.Vector3, plane: THREE.Plane): THREE.Vector3 | null {
    const denom = plane.normal.dot(point);
    if (Math.abs(denom) < 1e-8) return null;
    const t = -plane.constant / denom;
    if (t <= 0) return null;
    return point.clone().multiplyScalar(t);
}
